import { type EntityRoot } from '@dl/shared';
import type { ChildNode, Mountable, Renderable, Send } from '@llui/dom';
import { createRouter, route, type RouteDestination, type RouteLocation } from '@llui/router';
import { connectRouter, type RouterEffect } from '@llui/router/connect';

/**
 * App route — discriminated union. URL-backed via @llui/router so back/
 * forward work and deep-links survive reload.
 *
 * `AppRoute` is deliberately application-owned rather than storing the
 * framework's named `RouteLocation` in Model. The adapter below is the single
 * bidirectional seam between that domain shape and @llui/router's URL identity.
 *
 * Routes added in III.C.5 (#73) + III.C.8 (#92):
 *   - `draft-inbox` — GM-only draft queue
 *   - `admin-llm`   — system-admin LLM provider/model/key/binding UI;
 *                      non-admins get 404 server-side (the client
 *                      renders the existing 404/list fallback)
 *
 * Routes added in #117 (sign-in / sign-up / verify-email / reset-password):
 *   - `sign-in`           — unauthenticated entry surface
 *   - `sign-up`           — account creation
 *   - `forgot-password`   — request password-reset email
 *   - `verify-email`      — consume the email confirmation token
 *   - `reset-password`    — accept the reset token + new password
 */
/**
 * Campaign-settings sub-tabs. The settings page is a tabbed CRUD surface
 * (#schema-settings): `general` (pending-policy + AI extensions),
 * `campaign` (the typed Campaign Settings view — `config.set` ops per
 * #186 / ADR-0004 §6: accent color, calendars, prose voice, etc.),
 * `traits` (homebrew trait/predicate authoring — replaces the old modal),
 * `types` (custom entity types — `(root, traits[])` quick-create templates),
 * `calendars` (custom fictional-calendar authoring — `pack.define_calendar`
 * definitions + the canonical-calendar setting pointer, #177 / ADR-0004 §1/§4).
 * URL-backed so the trait picker can deep-link to `#/settings/traits` and
 * back/forward work.
 */
export type SettingsTab = 'general' | 'campaign' | 'traits' | 'types' | 'calendars';

const SETTINGS_TABS: readonly SettingsTab[] = [
  'general',
  'campaign',
  'traits',
  'types',
  'calendars',
];

/** Coerce an arbitrary URL segment into a known tab (unknown → general). */
export function asSettingsTab(value: string | undefined): SettingsTab {
  return SETTINGS_TABS.includes(value as SettingsTab) ? (value as SettingsTab) : 'general';
}

/** The six entity roots, in canonical order (mirrors `entity-roots.ts`). */
const ENTITY_ROOTS: readonly EntityRoot[] = [
  'location',
  'character',
  'faction',
  'event',
  'item',
  'lore',
];

/** Coerce a URL segment into a known entity root, or `undefined` (→ all roots). */
export function asEntityRoot(value: string | undefined): EntityRoot | undefined {
  return ENTITY_ROOTS.includes(value as EntityRoot) ? (value as EntityRoot) : undefined;
}

export type AppRoute =
  // `list` is the multi-root entity browser. Bare → the all-roots accordion
  // (`/`); with `root` → a single-root view (`/browse/:root`), driven by the
  // left-rail "Browse" links. The optional param mirrors the settings/
  // settings-tab split below (one `kind`, two routes — bare + param).
  | { kind: 'list'; root?: EntityRoot }
  | { kind: 'entity'; id: string }
  | { kind: 'settings'; tab?: SettingsTab }
  | { kind: 'a11y' }
  | { kind: 'ui-gallery' }
  | { kind: 'recovery' }
  | { kind: 'draft-inbox' }
  | { kind: 'trash' }
  | { kind: 'auditor' }
  // #258 — the chrome-stripped shared-display ("Table View") surface. Renders
  // the current table focus through the designated Viewer's projection. In a
  // second tab it is a pure broadcast renderer (no replica); side-by-side it
  // reads the same in-process state. Not auth-gated client-side (the substrate
  // shell still gates content) — a separate tab on this route is the dual-
  // screen transport.
  | { kind: 'table' }
  | { kind: 'admin-users' }
  | { kind: 'admin-llm' }
  | { kind: 'admin-signups' }
  | { kind: 'admin-audit' }
  | { kind: 'campaigns' }
  | { kind: 'account' }
  | { kind: 'sign-in' }
  | { kind: 'sign-up' }
  | { kind: 'forgot-password' }
  | { kind: 'verify-email'; token: string }
  | { kind: 'reset-password'; token: string }
  | { kind: '2fa-disable'; token: string };

/**
 * Named, bidirectional URL registry (@llui/router >=0.12). `list`/`browse`
 * and `settings`/`settingsTab` are separate URL identities that fold into the
 * same application route arms.
 */
const routes = {
  list: route('/'),
  browse: route('/browse/:root'),
  entity: route('/entity/:id'),
  settings: route('/settings'),
  settingsTab: route('/settings/:tab'),
  a11y: route('/a11y'),
  uiGallery: route('/ui-gallery'),
  recovery: route('/recovery'),
  draftInbox: route('/drafts'),
  trash: route('/trash'),
  auditor: route('/auditor'),
  table: route('/table'),
  adminUsers: route('/admin/users'),
  adminLlm: route('/admin/llm'),
  adminSignups: route('/admin/signups'),
  adminAudit: route('/admin/audit'),
  campaigns: route('/campaigns'),
  account: route('/account'),
  signIn: route('/sign-in'),
  signUp: route('/sign-up'),
  forgotPassword: route('/forgot-password'),
  verifyEmail: route('/verify-email/:token'),
  resetPassword: route('/reset-password/:token'),
  twofaDisable: route('/2fa-disable/:token'),
} as const;

export type AppRouterLocation = RouteLocation<typeof routes>;
interface AppRouterNavigateMessage {
  readonly type: 'navigate';
  readonly route: AppRoute;
}

// History (clean-URL) mode. Requires an SPA fallback at the edge so a hard
// navigation / reload to e.g. /entity/abc serves index.html. Vite supplies it
// in dev/preview; Cloudflare Pages uses apps/client/public/_redirects.
export const router = createRouter(routes, { mode: 'history' });
export const routing = connectRouter(router, {
  navigateMsg: (location): AppRouterNavigateMessage => ({
    type: 'navigate',
    route: appRouteFromLocation(location),
  }),
});

type AppRouteDestination = RouteDestination<typeof routes>;
type AppRouteHref = (...destination: AppRouteDestination) => string;
type AppRoutePush = (...destination: AppRouteDestination) => RouterEffect;
type AppRouteNavigate = (...destination: AppRouteDestination) => RouterEffect;

/** Fold one framework route identity into the application-owned route model. */
export function appRouteFromLocation(location: AppRouterLocation): AppRoute {
  switch (location.name) {
    case 'list':
      return { kind: 'list' };
    case 'browse': {
      const root = asEntityRoot(location.params.root);
      return root === undefined ? { kind: 'list' } : { kind: 'list', root };
    }
    case 'entity':
      return { kind: 'entity', id: location.params.id };
    case 'settings':
      return { kind: 'settings' };
    case 'settingsTab':
      return { kind: 'settings', tab: asSettingsTab(location.params.tab) };
    case 'a11y':
      return { kind: 'a11y' };
    case 'uiGallery':
      return { kind: 'ui-gallery' };
    case 'recovery':
      return { kind: 'recovery' };
    case 'draftInbox':
      return { kind: 'draft-inbox' };
    case 'trash':
      return { kind: 'trash' };
    case 'auditor':
      return { kind: 'auditor' };
    case 'table':
      return { kind: 'table' };
    case 'adminUsers':
      return { kind: 'admin-users' };
    case 'adminLlm':
      return { kind: 'admin-llm' };
    case 'adminSignups':
      return { kind: 'admin-signups' };
    case 'adminAudit':
      return { kind: 'admin-audit' };
    case 'campaigns':
      return { kind: 'campaigns' };
    case 'account':
      return { kind: 'account' };
    case 'signIn':
      return { kind: 'sign-in' };
    case 'signUp':
      return { kind: 'sign-up' };
    case 'forgotPassword':
      return { kind: 'forgot-password' };
    case 'verifyEmail':
      return { kind: 'verify-email', token: location.params.token };
    case 'resetPassword':
      return { kind: 'reset-password', token: location.params.token };
    case 'twofaDisable':
      return { kind: '2fa-disable', token: location.params.token };
  }
}

/** Convert the application-owned route into the router's exact named target. */
function appRouteDestination(routeValue: AppRoute): AppRouteDestination {
  switch (routeValue.kind) {
    case 'list':
      return routeValue.root === undefined ? ['list'] : ['browse', { root: routeValue.root }];
    case 'entity':
      return ['entity', { id: routeValue.id }];
    case 'settings':
      return routeValue.tab === undefined ? ['settings'] : ['settingsTab', { tab: routeValue.tab }];
    case 'a11y':
      return ['a11y'];
    case 'ui-gallery':
      return ['uiGallery'];
    case 'recovery':
      return ['recovery'];
    case 'draft-inbox':
      return ['draftInbox'];
    case 'trash':
      return ['trash'];
    case 'auditor':
      return ['auditor'];
    case 'table':
      return ['table'];
    case 'admin-users':
      return ['adminUsers'];
    case 'admin-llm':
      return ['adminLlm'];
    case 'admin-signups':
      return ['adminSignups'];
    case 'admin-audit':
      return ['adminAudit'];
    case 'campaigns':
      return ['campaigns'];
    case 'account':
      return ['account'];
    case 'sign-in':
      return ['signIn'];
    case 'sign-up':
      return ['signUp'];
    case 'forgot-password':
      return ['forgotPassword'];
    case 'verify-email':
      return ['verifyEmail', { token: routeValue.token }];
    case 'reset-password':
      return ['resetPassword', { token: routeValue.token }];
    case '2fa-disable':
      return ['twofaDisable', { token: routeValue.token }];
  }
}

/** Application-route adapters keep the domain model independent of RouterLocation. */
export function appRouteHref(routeValue: AppRoute): string {
  const href = router.href as AppRouteHref;
  return href(...appRouteDestination(routeValue));
}

export function appRoutePush(routeValue: AppRoute): RouterEffect {
  const push = routing.push as AppRoutePush;
  return push(...appRouteDestination(routeValue));
}

export function appRouteNavigate(routeValue: AppRoute): RouterEffect {
  const navigate = routing.navigate as AppRouteNavigate;
  return navigate(...appRouteDestination(routeValue));
}

export function appRouteLink<M>(
  send: Send<M>,
  routeValue: AppRoute,
  attrs: Record<string, unknown>,
  children: readonly ChildNode[],
  msgFactory: (route: AppRoute) => M,
): Mountable {
  type AppLinkArguments = [
    ...destination: AppRouteDestination,
    attrs: Record<string, unknown>,
    children: readonly ChildNode[],
    msgFactory: (location: AppRouterLocation) => M,
  ];
  const link = routing.link as (send: Send<M>, ...args: AppLinkArguments) => Mountable;
  const args = [
    ...appRouteDestination(routeValue),
    attrs,
    children,
    (location: AppRouterLocation) => msgFactory(appRouteFromLocation(location)),
  ] as AppLinkArguments;
  return link(send, ...args);
}

export function appRouteListener<M>(send: Send<M>, msgFactory: (route: AppRoute) => M): Renderable {
  return routing.listener<M, M>(
    send,
    (location) => msgFactory(appRouteFromLocation(location)),
    () => msgFactory({ kind: 'list' }),
  );
}

/** Stable identity string for an AppRoute — used as the `h.scope` key. */
export function routeKey(r: AppRoute): string {
  // `list:<root>` vs bare `list` must differ so the keyed list-arm remounts
  // when the Browse rail switches the active root filter.
  if (r.kind === 'list') return r.root !== undefined ? `list:${r.root}` : 'list';
  if (r.kind === 'entity') return `entity:${r.id}`;
  if (r.kind === 'settings') return `settings:${r.tab ?? 'general'}`;
  if (r.kind === 'a11y') return 'a11y';
  if (r.kind === 'ui-gallery') return 'ui-gallery';
  if (r.kind === 'recovery') return 'recovery';
  if (r.kind === 'draft-inbox') return 'draft-inbox';
  if (r.kind === 'trash') return 'trash';
  if (r.kind === 'auditor') return 'auditor';
  if (r.kind === 'table') return 'table';
  if (r.kind === 'admin-users') return 'admin-users';
  if (r.kind === 'admin-audit') return 'admin-audit';
  if (r.kind === 'admin-llm') return 'admin-llm';
  if (r.kind === 'admin-signups') return 'admin-signups';
  if (r.kind === 'campaigns') return 'campaigns';
  if (r.kind === 'account') return 'account';
  if (r.kind === 'sign-in') return 'sign-in';
  if (r.kind === 'sign-up') return 'sign-up';
  if (r.kind === 'forgot-password') return 'forgot-password';
  if (r.kind === 'verify-email') return `verify-email:${r.token}`;
  if (r.kind === 'reset-password') return `reset-password:${r.token}`;
  if (r.kind === '2fa-disable') return `2fa-disable:${r.token}`;
  return 'list';
}

/** Routes that render without a session — the app shell renders them
 * even before the boot resolution settles. */
export function isAuthRoute(r: AppRoute): boolean {
  return (
    r.kind === 'sign-in' ||
    r.kind === 'sign-up' ||
    r.kind === 'forgot-password' ||
    r.kind === 'verify-email' ||
    r.kind === 'reset-password' ||
    r.kind === '2fa-disable'
  );
}

/**
 * #258 — the chrome-stripped Table View route. Like the auth routes, it renders
 * in its OWN shell (no GM toolkit chrome: no header, no rail, no bottom nav, no
 * cursor HUD) — a player-facing shared display must not show GM affordances. The
 * top-level `branch` keys on this so the runtime mounts the Table shell.
 */
export function isTableRoute(r: AppRoute): boolean {
  return r.kind === 'table';
}

/** Initial route resolved from the current URL — used by component init.
 * History mode: the route lives in the pathname (e.g. `/entity/abc`). */
export function initialRoute(): AppRoute {
  if (typeof location === 'undefined') return { kind: 'list' };
  const matched = router.match(location.pathname || '/');
  return matched === null ? { kind: 'list' } : appRouteFromLocation(matched);
}
